import React from 'react'
import { Box } from '@mui/material'
import { Alert, AlertTitle } from '@mui/material'

/**
 * @typedef {object} InfoBoxProps
 * @property {'error' | 'warning' | 'info' | 'success'} severity - El tipo de alerta que determina el color y el icono.
 * @property {string} title - El título de la alerta.
 * @property {React.ReactNode} message - El contenido del mensaje de la alerta.
 * @property {boolean} [hidden=false] - Si es true, el componente no renderiza nada.
 * @property {React.CSSProperties} [style] - Estilos CSS personalizados para el componente Alert.
 * @property {React.ReactNode} [action=null] - Componente de acción adicional que se muestra en la parte inferior derecha de la alerta.
 * @property {boolean} [closable=false] - Si es true, muestra un botón de cerrar en la alerta.
 * @property {function(): void} [onClose=null] - Callback que se ejecuta cuando se hace clic en el botón de cerrar.
 * @property {string} [className=''] - Clase CSS adicional para el componente Alert.
 */

/**
 * Un componente de caja de información basado en Alert de Material-UI.
 *
 * Proporciona una forma visualmente atractiva de mostrar mensajes informativos, advertencias,
 * errores o éxitos al usuario. Incluye soporte para un título, contenido del mensaje,
 * acciones personalizadas y capacidad de cerrar la alerta.
 *
 * @component
 * @example
 * const MyComponent = () => {
 *   const [showAlert, setShowAlert] = useState(true);
 *
 *   const handleClose = () => {
 *     setShowAlert(false);
 *   };
 *
 *   return (
 *     <InfoBox
 *       severity="warning"
 *       title="Atención"
 *       message="Esta acción no se puede deshacer. ¿Desea continuar?"
 *       closable={true}
 *       onClose={handleClose}
 *       hidden={!showAlert}
 *       action={<ThemeButton onClick={handleContinue}>Continuar</ThemeButton>}
 *     />
 *   );
 * };
 *
 * @param {InfoBoxProps} props - Las props del componente InfoBox.
 */
export const InfoBox = ({
  severity,
  title,
  message,
  hidden = false,
  style,
  action = null,
  closable = false,
  onClose = null,
  className = '',
}) => {
  if (hidden) return null
  return (
    <Alert
      severity={severity}
      style={style ? style : null}
      onClose={closable && onClose}
      className={className}
    >
      <AlertTitle>{title}</AlertTitle>
      <Box display="flex" flexDirection="column">
        {message}
        <Box display="flex" justifyContent="flex-end" width="100%">
          {action}
        </Box>
      </Box>
    </Alert>
  )
}
